import { AuthenticatedUser, ISchedule, IUserFull } from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { canScheduleOthers } from "@gettactic/helpers/src/permissions";
import { mapWorkplaceRequestToReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";
import { ReserveModalDisclosureOptions } from "./types";

export const getDuplicateReservationData = async ({
  item,
  user,
  isWorkplaceRequestsAllowed,
  api,
  shouldSelectResource = false,
  authenticatedUser
}: {
  item: ISchedule | null;
  user?: AuthenticatedUser | IUserFull;
  isWorkplaceRequestsAllowed: boolean;
  api: any;
  shouldSelectResource?: boolean;
  authenticatedUser?: AuthenticatedUser;
}): Promise<ReserveModalDisclosureOptions> => {
  if (!item) {
    return {
      resourceType: "desk",
      office: null,
      floorId: null,
      resource: null
    };
  }
  const isMeetingRoom = item.resource.type === "meeting_room";
  // The copy keeps whoever the original was for: a meeting's organizer —
  // someone else's when it was booked on their behalf — or the person a desk
  // was booked for. Only without one, or for someone who can't book for
  // others (the copy would be refused), does it fall back to the caller's user.
  const fallbackOwner = ((user as AuthenticatedUser)?.user ?? user) as
    | IUserFull
    | undefined;
  const originalOwner = isMeetingRoom ? item.organizer : item.slots[0]?.user;
  const owner =
    authenticatedUser && canScheduleOthers(authenticatedUser)
      ? (originalOwner ?? fallbackOwner)
      : fallbackOwner;
  // Everyone else on the meeting, once each: the organizer is the owner.
  const attenders = isMeetingRoom
    ? item.slots
        .map((slot) => slot.user)
        .filter(
          (attender, index, all) =>
            !!attender &&
            attender.id !== owner?.id &&
            all.findIndex((other) => other?.id === attender.id) === index
        )
    : undefined;
  let workplaceRequestItems: any;
  let workplaceRequestTypes: any;

  if (isWorkplaceRequestsAllowed) {
    try {
      workplaceRequestItems =
        await api.client.workplaceRequests.getWorkplaceRequestsBySchedule(
          item.id
        );
      workplaceRequestTypes =
        await api.client.workplaceRequestTypes.getWorkplaceRequestTypes();
    } catch (error) {
      console.warn("Failed to fetch workplace requests for duplication", error);
    }
  }

  // Calculate duration from original schedule
  // For meeting rooms, use the slot's start/end times instead of main schedule times
  let startTime: Date;
  let endTime: Date;

  if (item.slots.length > 0) {
    // Use the first slot's times for meeting rooms
    startTime = new Date(dateStringToLocalTime(item.slots[0].start));
    endTime = new Date(dateStringToLocalTime(item.slots[0].end));
  } else {
    // Use main schedule times for other resource types
    startTime = new Date(dateStringToLocalTime(item.start));
    endTime = new Date(dateStringToLocalTime(item.end));
  }

  const duration = Math.round(
    (endTime.getTime() - startTime.getTime()) / (1000 * 60)
  ); // duration in minutes

  return {
    title: item.title || undefined,
    description: item.description || undefined,
    visibility: item.visibility,
    onlineMeetingProvider: item.online_meeting?.provider ?? null,
    resourceType: item.resource.type,
    office: authenticatedUser?.offices?.byId?.[item.resource.office_id] ?? null,
    floorId: item.resource.parent_id,
    resource: shouldSelectResource ? item.resource : null, // Don't pre-select resource to allow office/floor selection unless requested
    start: new Date(
      startTime.getFullYear(),
      startTime.getMonth(),
      startTime.getDate()
    ),
    time: startTime,
    duration: duration,
    workplaceRequestItems: isWorkplaceRequestsAllowed
      ? mapWorkplaceRequestToReserveFormData(
          workplaceRequestItems?.result || [],
          workplaceRequestTypes?.result?.elements || [],
          false
        )
      : undefined,
    attenders,
    owner
  };
};
